import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImagePlus, Loader2, Sparkles, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImg } from "@/lib/api";
import { POSTER_STYLES, errMsg } from "./mk";

const poll = async (jobId) => {
  for (let i = 0; i < 60; i++) {
    await new Promise((r) => setTimeout(r, 3000));
    const { data } = await api.get(`/admin/marketing/jobs/${jobId}`);
    if (data.status !== "running") return data;
  }
  return { status: "failed", error: "Poster took too long. Please try again." };
};

const Thumbs = ({ title, items, onPick, testPrefix }) => items.length > 0 && (
  <div className="min-w-0">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{title}</p>
    <div className="flex gap-2 overflow-x-auto pb-1">
      {items.map((p, i) => (
        <button type="button" key={p + i} onClick={() => onPick(p)} data-testid={`${testPrefix}-${i}`}
          className="h-14 w-14 shrink-0 overflow-hidden rounded-lg ring-1 ring-slate-200 hover:ring-2 hover:ring-blue-600 transition-shadow">
          <img src={resolveImg(p)} alt="" className="h-full w-full object-cover" />
        </button>
      ))}
    </div>
  </div>
);

export const PosterMaker = ({ language, seed, value, onChange }) => {
  const [headline, setHeadline] = useState("");
  const [details, setDetails] = useState("");
  const [style, setStyle] = useState("Admission");
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => { if (seed?.headline) { setHeadline(seed.headline); setDetails(seed.details || ""); } }, [seed]);
  useEffect(() => {
    api.get("/gallery").then(({ data }) => setGallery(data.map((g) => g.image).filter(Boolean).slice(0, 20))).catch(() => {});
    api.get("/admin/marketing/posters").then(({ data }) => setRecent(data)).catch(() => {});
  }, []);

  const generate = async () => {
    if (!headline.trim()) { toast.error("Enter a poster headline"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/marketing/generate-poster", { headline, details, language, style, reference_path: ref });
      const res = await poll(data.job_id);
      if (res.status === "done") { onChange(res.image_path); setRecent((r) => [res.image_path, ...r]); toast.success("Poster ready — please check the spelling"); }
      else toast.error(res.error || "Poster failed");
    } catch (e) { toast.error(errMsg(e, "Poster failed")); }
    finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/upload", fd); onChange(data.path); toast.success("Image added"); }
    catch (e) { toast.error(errMsg(e, "Upload failed")); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4 min-w-0" data-testid="poster-maker">
      <div className="flex items-center gap-2"><ImagePlus className="h-4 w-4 text-blue-700" /><h3 className="font-bold text-slate-900">AI Poster Maker</h3></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">Poster headline</Label><Input value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="Admissions Open 2026–27" data-testid="poster-headline" /></div>
        <div><Label className="text-xs">Style</Label>
          <Select value={style} onValueChange={setStyle}><SelectTrigger data-testid="poster-style"><SelectValue /></SelectTrigger>
            <SelectContent>{POSTER_STYLES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <div><Label className="text-xs">Supporting text (optional)</Label><Input value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Nursery to Std 10 • Limited seats" data-testid="poster-details" /></div>
      <Thumbs title="Use a gallery photo (optional)" items={gallery} onPick={(p) => setRef(ref === p ? "" : p)} testPrefix="poster-ref" />
      {ref && <p className="text-xs text-blue-700 flex items-center gap-1" data-testid="poster-ref-selected">Photo selected <button type="button" onClick={() => setRef("")}><X className="h-3 w-3" /></button></p>}
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={generate} disabled={busy} className="bg-blue-800 hover:bg-blue-900" data-testid="poster-generate-btn">
          {busy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Designing… (~30s)</> : <><Sparkles className="mr-1 h-4 w-4" /> Create poster</>}
        </Button>
        <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-slate-200 px-3 text-sm font-medium hover:bg-slate-50" data-testid="poster-upload-btn">
          <Upload className="h-4 w-4" /> Upload own<input type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        {value && <Button type="button" variant="ghost" onClick={() => onChange("")} data-testid="poster-remove-btn">Remove image</Button>}
      </div>
      <Thumbs title="Recent posters" items={recent} onPick={onChange} testPrefix="poster-recent" />
    </div>
  );
};
